import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/contexts/AuthContext'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { ToastProvider } from '@/contexts/ToastContext'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { HomeRedirect } from '@/components/HomeRedirect'
import { LoginPage } from '@/pages/LoginPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { DeliveredFleetPage } from '@/pages/DeliveredFleetPage'

import { SettingsPage } from '@/pages/SettingsPage'
import { ContactsPage } from '@/pages/ContactsPage'
import { AuditLogsPage } from '@/pages/AuditLogsPage'
import { LicenseManagementPage } from '@/pages/LicenseManagementPage'
import { PentestHistoryPage } from '@/pages/PentestHistoryPage'
import { EngagementDetailPage } from '@/pages/EngagementDetailPage'
import { ReleaseNotesPage } from '@/pages/ReleaseNotesPage'
import { ReleasesIndexPage } from '@/pages/ReleasesIndexPage'
import { PentestReleasesPage } from '@/pages/PentestReleasesPage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { AcceptInvitePage } from '@/pages/AcceptInvitePage'
import { ChangePasswordPage } from '@/pages/ChangePasswordPage'
import { MocOverviewPage } from '@/pages/MocOverviewPage'
import { MocDetailPage } from '@/pages/MocDetailPage'
import { PublicChangeRequestPage } from '@/pages/PublicChangeRequestPage'
import { FirewallOverviewPage } from '@/pages/FirewallOverviewPage'
import { FirewallDetailPage } from '@/pages/FirewallDetailPage'
import { PublicFirewallChangeRequestPage } from '@/pages/PublicFirewallChangeRequestPage'
import { RoleManagementPage } from '@/pages/RoleManagementPage'
import { GenerateTPPage } from '@/pages/GenerateTPPage'
import { CreateProjectPage } from '@/pages/CreateProjectPage'
import { ProjectDetailPage } from '@/pages/ProjectDetailPage'
import { ImportWorkbookPage } from '@/pages/ImportWorkbookPage'
import { SystemDetailPage } from '@/pages/SystemDetailPage'
import { SuperAdminForcePasswordPage } from '@/pages/SuperAdminForcePasswordPage'
import { SystemsPage } from '@/pages/SystemsPage'
import { KnowledgeWorkspacePage } from '@/pages/KnowledgeWorkspacePage'
import { NmsConfigurationsPage } from '@/pages/NmsConfigurationsPage'

// create a client for react query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
})

// wrapper component to use hooks inside router
function AppRoutes() {
  console.log('[APP] AppRoutes rendering...')
  
  return (
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              
              {/* public route for change request submission */}
              <Route path="/submit-change-request" element={<PublicChangeRequestPage />} />
              <Route path="/submit-firewall-change" element={<PublicFirewallChangeRequestPage />} />
              
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePasswordPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />

              <Route path="/systems" element={<ProtectedRoute><SystemsHomeRedirect /></ProtectedRoute>} />
              <Route path="/risk-assessment" element={<ProtectedRoute><SystemsPage /></ProtectedRoute>} />
              <Route path="/workspace" element={<ProtectedRoute><KnowledgeWorkspacePage /></ProtectedRoute>} />
              <Route path="/system-library" element={<ProtectedRoute><WorkspaceRedirect /></ProtectedRoute>} />
              <Route path="/document-pile" element={<Navigate to="/workspace" replace />} />
              <Route path="/systems-190" element={<ProtectedRoute><WorkspaceRedirect /></ProtectedRoute>} />
              <Route path="/asset-inventory" element={<Navigate to="/dashboard" replace />} />
              <Route path="/nms-configurations" element={<ProtectedRoute><NmsConfigurationsPage /></ProtectedRoute>} />
              <Route
                path="/search"
                element={
                  <ProtectedRoute>
                    <SystemsHomeRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/contacts"
                element={
                  <ProtectedRoute>
                    <ContactsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audit-logs"
                element={
                  <ProtectedRoute requiredRole="super_admin">
                    <AuditLogsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/license-management"
                element={
                  <ProtectedRoute requiredRole="super_admin">
                    <LicenseManagementPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/force-password-reset"
                element={
                  <ProtectedRoute requiredRole="super_admin">
                    <SuperAdminForcePasswordPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/roles"
                element={
                  <ProtectedRoute requiredRole="super_admin">
                    <RoleManagementPage />
                  </ProtectedRoute>
                }
              />

              {/* Round 2 cloud-upload pages, replace the legacy
                  /network-data Pentest History feature. sidebar
                  label "Pentest History" unchanged, only the
                  underlying route + data source changed */}
              <Route
                path="/pentest-history"
                element={
                  <ProtectedRoute>
                    <PentestHistoryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pentest-history/:id"
                element={
                  <ProtectedRoute>
                    <EngagementDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* redirect legacy /network-data, keeps in-flight
                  bookmarks landing on the new list view */}
              <Route path="/network-data" element={<Navigate to="/pentest-history" replace />} />

              {/* legacy route removed, AI dedup page */}
              <Route path="/deduplication" element={<Navigate to="/dashboard" replace />} />

              <Route
                path="/pentest-releases"
                element={
                  <ProtectedRoute>
                    <PentestReleasesPage />
                  </ProtectedRoute>
                }
              />

              {/* old /release-notes index was Express API (often empty). pentest tool data is Supabase on /pentest-releases */}
              <Route
                path="/release-notes"
                element={
                  <ProtectedRoute>
                    <Navigate to="/pentest-releases" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/platform-releases"
                element={
                  <ProtectedRoute>
                    <ReleasesIndexPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/release-notes/:version"
                element={
                  <ProtectedRoute>
                    <ReleaseNotesPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/moc"
                element={
                  <ProtectedRoute>
                    <MocOverviewPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/moc/:id"
                element={
                  <ProtectedRoute>
                    <MocDetailPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/firewall"
                element={
                  <ProtectedRoute>
                    <FirewallOverviewPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/firewall/:id"
                element={
                  <ProtectedRoute>
                    <FirewallDetailPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/delivered-fleet"
                element={
                  <ProtectedRoute>
                    <DeliveredFleetPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/generate-tp"
                element={
                  <ProtectedRoute>
                    <GenerateTPPage />
                  </ProtectedRoute>
                }
              />

              {/* ── Shipyard nav, redirect to dashboard explorer ── */}
              <Route path="/shipyards" element={<Navigate to="/dashboard" replace />} />
              <Route path="/shipyards/:shipyardId" element={<Navigate to="/dashboard" replace />} />

              {/* ── Project list, redirect to dashboard explorer ── */}
              <Route path="/projects" element={<Navigate to="/dashboard" replace />} />

              {/* ── Project-first workflow ── */}
              <Route
                path="/projects/new"
                element={
                  <ProtectedRoute>
                    <CreateProjectPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:id"
                element={
                  <ProtectedRoute>
                    <ProjectDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/systems/:systemId"
                element={
                  <ProtectedRoute>
                    <SystemDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:id/import"
                element={
                  <ProtectedRoute>
                    <ImportWorkbookPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <HomeRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path="*"
                element={
                  <ProtectedRoute>
                    <HomeRedirect />
                  </ProtectedRoute>
                }
              />
            </Routes>
  )
}

function WorkspaceRedirect() {
  const location = useLocation()
  return <Navigate to={`/workspace${location.search}`} replace />
}

function SystemsHomeRedirect() {
  const location = useLocation()
  return <Navigate to={`/workspace${location.search}`} replace />
}

function App() {
  console.log('[APP] App component rendering...')
  
  return (
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <ToastProvider>
              <Router>
                {/* a render error used to blank the whole page with no
                    message; now it shows what threw and reports it */}
                <ErrorBoundary where="app">
                  <AppRoutes />
                </ErrorBoundary>
              </Router>
            </ToastProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
  )
}

export default App
